import { useState } from "react";
import type * as React from "react";
import { Box, Divider, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import EmailIcon from "@mui/icons-material/Email";
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";
import PageTitle from "utils/PageTitle";
import Translate from "translate/Translate";
import { trackPasswordResetRequested } from "analytics";
import { openGLSnackbar } from "store/reducers/snackbar/openGLSnackbarSlice";
import { selectUserPersonalInformation } from "store/reducers/user/UserAccountSlice";
import GradientFramedLayout from "components/v3/layouts/GradientFramedLayout";
import BreadcrumbTrail from "components/v3/sharedComponents/BreadcrumbTrail";
import { PATHS } from "constants/pathConstants";
import GLButton from "components/v3/sharedComponents/GLButton";
import AccountSecurityCallout from "components/v3/pages/account/AccountManagementPage/AccountSecurityCallout";
import { COLORS } from "components/v3/Theme/colors";

const PAGE_TITLE = "Reset Password";
const PAGE_HEADING = "Change password";

const changePasswordCardSx = {
  backgroundColor: COLORS.BACKGROUND_BLUE,
  borderRadius: "12px",
  boxSizing: "border-box",
  maxWidth: "100%",
  minWidth: 0,
  padding: 2.5,
  width: "100%"
} as const;

const ChangePasswordPage: React.FC = () => {
  const dispatch = useDispatch();
  const userEmail = useSelector(selectUserPersonalInformation).email;
  const [emailSent, setEmailSent] = useState(false);
  const [sending, setSending] = useState(false);

  const { REACT_APP_AUTH0_DOMAIN } = process.env;
  const { REACT_APP_AUTH0_CLIENT_ID } = process.env;
  const { REACT_APP_AUTH0_CONNECTION } = process.env;

  PageTitle(PAGE_TITLE);

  const sendPasswordChangeEmail = () => {
    const options = {
      method: "POST" as const,
      url: `https://${REACT_APP_AUTH0_DOMAIN}/dbconnections/change_password`,
      headers: { "content-type": "application/json" },
      data: {
        client_id: REACT_APP_AUTH0_CLIENT_ID,
        email: userEmail,
        connection: REACT_APP_AUTH0_CONNECTION
      }
    };

    setSending(true);
    axios
      .request(options)
      .then(() => {
        setEmailSent(true);
        trackPasswordResetRequested("success");
      })
      .catch((error: unknown) => {
        trackPasswordResetRequested("failure", error);
        dispatch(
          openGLSnackbar({
            variant: "error",
            subText:
              "Something went wrong sending this email, please try again and reach out to us if you need to!"
          })
        );
      })
      .finally(() => {
        setSending(false);
      });
  };

  return (
    <GradientFramedLayout showHeader={true}>
      <Box
        sx={{
          boxSizing: "border-box",
          display: "flex",
          flex: 1,
          flexDirection: "column",
          minHeight: 0,
          width: "100%"
        }}
      >
        <Box
          sx={{
            alignItems: "center",
            boxSizing: "border-box",
            display: "flex",
            flex: 1,
            flexDirection: "column",
            gap: 3,
            marginX: "auto",
            maxWidth: 500,
            minWidth: 0,
            paddingBottom: "40px",
            paddingTop: { md: 3, xs: 2 },
            paddingX: { md: 3, xs: 2 },
            width: "100%"
          }}
        >
          <Typography
            variant="h4_primary"
            component="h1"
            sx={{ fontWeight: 700, textAlign: "left", width: "100%" }}
          >
            <Translate text={PAGE_HEADING} />
          </Typography>

          <BreadcrumbTrail
            segments={[
              { label: "Account", to: PATHS.ACCOUNT_MANAGEMENT },
              {
                label: <Translate text="Privacy & Security" />,
                to: PATHS.ACCOUNT_MANAGEMENT_PRIVACY_AND_SECURITY
              },
              { label: <Translate text={PAGE_HEADING} /> }
            ]}
          />

          <Box sx={changePasswordCardSx}>
            {emailSent ? null : (
              <>
                <Box sx={{ textAlign: "center", width: "100%" }}>
                  <Typography
                    variant="body2"
                    sx={{ color: COLORS.DARK_GRAY_2, lineHeight: 1.65, mb: 4 }}
                  >
                    <Translate text="Click the button below and we'll send you an email with a link to change your password." />
                  </Typography>
                </Box>
              </>
            )}

            <Box
              sx={{
                alignItems: "center",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                textAlign: "center",
                width: "100%"
              }}
            >
              {emailSent ? (
                <Typography
                  variant="body2"
                  sx={{ color: COLORS.DARK_GRAY_2, lineHeight: 1.65 }}
                >
                  <Translate text="Email sent! Keep an eye on your inbox, and don't forget to check you junk mail folder if you haven't heard from us in a few minutes." />
                </Typography>
              ) : (
                <GLButton
                  buttonType="primary"
                  loading={sending}
                  leftIcon={
                    <EmailIcon
                      sx={{ color: COLORS.WHITE, fontSize: "1.25rem" }}
                    />
                  }
                  onClick={sendPasswordChangeEmail}
                  text="Send password change email"
                  sx={{
                    alignItems: "center",
                    alignSelf: "center",
                    boxSizing: "border-box",

                    minHeight: "auto",
                    px: 2,
                    py: 1.5,

                    "& .MuiTypography-root": {
                      flex: "1 1 auto",
                      lineHeight: 1.35,
                      minWidth: 0,
                      textAlign: "center",
                      whiteSpace: "normal",
                      fontSize: "16px"
                    }
                  }}
                />
              )}
            </Box>
          </Box>

          <Box
            sx={{
              flexShrink: 0,
              marginTop: "auto",
              width: "100%"
            }}
          >
            <AccountSecurityCallout />
          </Box>
        </Box>
      </Box>
    </GradientFramedLayout>
  );
};

export default ChangePasswordPage;
